/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import { CheckmarkIcon, Skeleton, Text } from "@packages/ui";
import * as RadixUIAvatar from "@radix-ui/react-avatar";
import type { ComponentProps } from "react";
import { useMediaQuery } from "usehooks-ts";

import { Screens } from "../../lib/enums/screens";

const cssAvatarImage: Interpolation = {
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  width: "100%",
};
const cssAvatarImageContainer: Interpolation<ThemeType> = (theme) => ({
  borderRadius: theme.radius.full,
  overflow: "hidden",
  height: 88,
  position: "relative",
  width: 88,
  [`@media ${Screens.Large}`]: {
    height: 116,
    width: 116,
  },
});
const cssAvatarRoot: Interpolation = {
  position: "relative",
};
const cssSelectedIcon: Interpolation<ThemeType> = (theme) => ({
  position: "absolute",
  right: theme.spacing.none,
  bottom: theme.spacing.none,
  background: theme.semanticColors.background,
  borderWidth: theme.stroke.light,
  borderStyle: "solid",
  borderColor: theme.semanticColors.background,
  borderRadius: theme.radius.full,
});
const cssDivContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.m,
  justifyContent: "center",
  width: "100%",
  [`@media ${Screens.Large}`]: {
    gap: theme.spacing.l,
  },
});
const cssDivTextContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xxs,
  justifyContent: "center",
  textAlign: "center",
  width: "100%",
});
const cssSkeletonAvatarImage: Interpolation = {
  height: "100%",
  width: "100%",
};
const cssSkeletonName: Interpolation<ThemeType> = (theme) => ({
  borderRadius: theme.radius.xxs,
  height: 20,
  width: "100%",
  [`@media ${Screens.Large}`]: {
    height: theme.spacing.l,
  },
});
const cssSpanKid: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.accentMedium,
  bottom: 0,
  color: theme.colors.white,
  display: "flex",
  fontFamily: theme.fontFamilies.sansSerif,
  fontSize: 12,
  fontWeight: 700,
  justifyContent: "center",
  left: -8,
  paddingBottom: theme.spacing.s,
  paddingTop: 3,
  position: "absolute",
  transform: "rotate(16deg)",
  width: "100%",
  [`@media ${Screens.Large}`]: {
    fontSize: 16,
    left: -12,
    paddingTop: theme.spacing.xxs,
  },
});
const cssTextMetadata: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});

export const Avatar = ({
  identifier,
  imgSrc,
  metadata,
  name,
  selected = false,
  ...props
}: {
  identifier?: string;
  imgSrc: string;
  metadata?: string;
  name: string;
  selected?: boolean;
} & ComponentProps<typeof RadixUIAvatar.Root>) => {
  const isLarge = useMediaQuery(Screens.Large);

  return (
    <div css={cssDivContainer}>
      <RadixUIAvatar.Root css={cssAvatarRoot} {...props}>
        <div css={cssAvatarImageContainer}>
          <RadixUIAvatar.Image alt={name} css={cssAvatarImage} src={imgSrc} />
          <RadixUIAvatar.Fallback asChild>
            <Skeleton css={cssSkeletonAvatarImage} />
          </RadixUIAvatar.Fallback>
          {identifier && <span css={cssSpanKid}>{identifier}</span>}
        </div>
        {selected && (
          <CheckmarkIcon.FilledOn
            css={cssSelectedIcon}
            size={isLarge ? 32 : 24}
          />
        )}
      </RadixUIAvatar.Root>
      <div css={cssDivTextContainer}>
        {name ? (
          <Text overflow="ellipsis" size={isLarge ? "l" : "m"} type="title">
            {name}
          </Text>
        ) : (
          <Skeleton css={cssSkeletonName} />
        )}
        {metadata && (
          <Text
            css={cssTextMetadata}
            overflow="ellipsis"
            size={isLarge ? "xl" : "l"}
            type="detail"
          >
            {metadata}
          </Text>
        )}
      </div>
    </div>
  );
};
